import { useEffect, type DependencyList } from 'react'

/**
 * Run a fetching effect, and ignore its result once it has been superseded.
 *
 * A plain `useEffect` that fetches has no way to know it has been re-fired.
 * The first request keeps running, and whichever response happens to arrive
 * last wins — so a slow response can overwrite a newer one, and re-fires stack
 * instead of replacing each other. Every stacked request holds a backend DB
 * connection for its whole life, against a pool of 15 served by one uvicorn
 * worker.
 *
 * That is not hypothetical here: on 2026-09-15 the Priser grid re-fired ~190
 * times a minute, the overlapping requests took every pool slot, and logins
 * started failing on QueuePool checkout while the app looked down.
 *
 * `alive()` is false once the effect has been re-fired or the component has
 * unmounted. Guard every state write with it:
 *
 *     useLatest(alive => {
 *       setLoading(true)
 *       fetchOversikt(filters)
 *         .then(r => { if (alive()) setData(r.data) })
 *         .finally(() => { if (alive()) setLoading(false) })
 *     }, [filterKey])
 *
 * This does not abort the HTTP request — it only stops a superseded response
 * from being applied. Abandoning the connection is what the axios timeout does.
 *
 * `run` may return a cleanup, which runs on the same schedule as a `useEffect`
 * cleanup — used by the debounced typeaheads to clear their pending timer.
 *
 * Dependencies follow the same rules as `useEffect`, so keep them primitive:
 * an object or array literal is rebuilt every render and will re-fire this on
 * every render. Pass a `*Key` string (see `filterKey`, `appliedKey`) instead.
 */
export function useLatest(
  run: (alive: () => boolean) => void | (() => void),
  deps: DependencyList,
): void {
  useEffect(() => {
    let alive = true
    const cleanup = run(() => alive)
    return () => {
      alive = false
      cleanup?.()
    }
    // `run` is intentionally excluded: it is an inline closure, so including it
    // would re-fire on every render — the very bug this exists to prevent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}
